const KEY = 'b2b:login_attempt';
const TTL_MS = 10 * 60 * 1000;

export interface LoginAttempt {
  id: string;
  startedAt: number;
}

/**
 * Correlates `auth_login_initiated` with its outcome across the WorkOS round
 * trip.
 *
 * Needed because the funnel cannot be joined on identity: the initiated event
 * is captured while anonymous, and with `person_profiles: 'identified_only'`
 * PostHog never attaches it to a person, nor retroactively merges it when
 * `identify()` runs on the way back. A person-aggregated funnel would show
 * ~100% drop-off at step 1. Joining on this id instead is exact, survives the
 * identity change, and yields real round-trip latency.
 *
 * sessionStorage is per-tab and per-origin, and survives a top-level navigation
 * away and back to the same origin — which is exactly the WorkOS flow.
 */
const readAttempt = (): LoginAttempt | null => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (raw === null) return null;

    const attempt = JSON.parse(raw) as LoginAttempt;
    if (typeof attempt?.id !== 'string') return null;

    // A stale id must not get stapled to an unrelated success an hour later.
    if (Date.now() - attempt.startedAt > TTL_MS) {
      sessionStorage.removeItem(KEY);
      return null;
    }

    return attempt;
  } catch {
    // Private mode / disabled storage — degrade to an unjoinable funnel rather
    // than breaking login.
    return null;
  }
};

export const startLoginAttempt = (): LoginAttempt => {
  const attempt: LoginAttempt = {
    id:
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    startedAt: Date.now()
  };

  try {
    sessionStorage.setItem(KEY, JSON.stringify(attempt));
  } catch {
    // See above — the id still rides on the initiated event.
  }

  return attempt;
};

/** Read without clearing. Use when the attempt may still resolve. */
export const peekLoginAttempt = (): LoginAttempt | null => readAttempt();

/** Read and clear. Use at a terminal outcome for the attempt. */
export const consumeLoginAttempt = (): LoginAttempt | null => {
  const attempt = readAttempt();
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing to do; the TTL will eventually make it stale.
  }
  return attempt;
};
